import { Icon, IconButton } from "@evergis/ui";
import React, { FC, useCallback, useMemo } from "react";
import EasyMDE from "easymde";
import { DropResult } from "react-beautiful-dnd";
import { AttributesUpdate } from "../../../../../../hooks/attributes/useAttributes";
import { reoderList } from "../../../../../../utils/array";
import {
  AddBlock,
  DeleteContainer,
  InfoBlock,
  ProjectInfoContainer,
  StyledDndAnchor,
  StyledDraggableList,
} from "../styled";
import { AddInfoBlock } from "./AddInfoBlock";
import { EmptyBlock } from "./EmptyBlock";
import { GalleryBlock } from "./GalleryBlock";
import { MarkdownEditor } from "./MarkdownEditor";
import { GallerySettings, ProjectInfoBlock, ProjectInfoBlockType } from "./types";
import { InfoBlockContainer } from "./styled";

export interface ProjectInfoProps extends AttributesUpdate<ProjectInfoBlock> {
  blocks?: ProjectInfoBlock[];
}

export const ProjectInfo: FC<ProjectInfoProps> = ({
  blocks = [],
  updateAttribute,
  setAttributes,
  addAttribute,
  deleteAttribute,
}) => {
  const onDragEnd = useCallback(
    ({ source, destination }: DropResult) => {
      if (!destination) return;
      const newAttrs = reoderList(blocks, source.index, destination.index);

      setAttributes(newAttrs);
    },
    [blocks, setAttributes],
  );

  const customRendererOptions = useMemo(() => {
    return {
      toolbar: ["bold", "italic", "heading", "|", "quote", "unordered-list", "ordered-list", "|", "link"],
      status: false,
      spellChecker: false,
      autofocus: true,
    } as EasyMDE.Options;
  }, []);

  if (blocks.length === 0) {
    return (
      <ProjectInfoContainer>
        <EmptyBlock add={addAttribute} setItems={setAttributes} items={blocks} />
      </ProjectInfoContainer>
    );
  }

  return (
    <ProjectInfoContainer>
      <StyledDraggableList
        onDragEnd={onDragEnd}
        id={"attribute-list"}
        items={blocks as unknown as Record<string, unknown>[]}
        getId={item => (item as unknown as ProjectInfoBlock)?.id}
      >
        {(attribute, provided, snapshot, index) => (
          <InfoBlockContainer>
            <InfoBlock ref={provided.innerRef} {...provided.draggableProps} isDragging={snapshot.isDragging}>
              <StyledDndAnchor {...provided.dragHandleProps} visible>
                <Icon kind={"dragdrop"} />
              </StyledDndAnchor>
              {attribute.type === ProjectInfoBlockType.MDE && (
                <MarkdownEditor
                  value={(attribute as unknown as ProjectInfoBlock).value as string}
                  onChange={value => updateAttribute((attribute as unknown as ProjectInfoBlock).id, { value })}
                  options={customRendererOptions}
                />
              )}
              {attribute.type === ProjectInfoBlockType.GALLERY && (
                <GalleryBlock
                  value={(attribute as unknown as ProjectInfoBlock).value as GallerySettings}
                  onChange={value => updateAttribute((attribute as unknown as ProjectInfoBlock).id, { value })}
                />
              )}
              <DeleteContainer>
                <IconButton
                  onClick={() => deleteAttribute((attribute as unknown as ProjectInfoBlock).id)}
                  kind={"delete"}
                />
              </DeleteContainer>
            </InfoBlock>
            <AddBlock>
              <AddInfoBlock index={index + 1} add={addAttribute} setItems={setAttributes} items={blocks} />
            </AddBlock>
          </InfoBlockContainer>
        )}
      </StyledDraggableList>
    </ProjectInfoContainer>
  );
};
